// The "open in your browser" hint inside Instagram's and (14 Oct) TikTok's in-app browsers, and on a computer a QR to carry on on a phone.
// Offered, never required (spec: Instagram's browser), and shown once per device: remembered in localStorage, which
// Instagram's browser can wipe, and for this visit in memory too, so the join page and the game never both show it.
import { useEffect, useState } from 'react';
import { ExternalLink, Smartphone } from 'lucide-react';
import { Qr } from '@/components/Qr';

export type BrowserHintKind = 'instagram' | 'tiktok' | 'desktop';

/**
 * Where the page is open: Instagram's in-app browser, a computer (worth a QR to the phone), or anywhere else (nothing
 * to say). iPadOS reports itself as a Mac, so a Mac with a touch screen counts as a tablet, not a computer.
 */
export function browserHintKind(ua: string, touchPoints = 0): BrowserHintKind | undefined {
  if (/Instagram/i.test(ua)) return 'instagram';
  // 14 Oct: TikTok's in-app browser (the bio link opens in it): Android says musical_ly or BytedanceWebview, iOS musical_ly.
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  if (/iPhone|iPad|iPod|Android|Mobile/i.test(ua)) return undefined;
  if (/Macintosh/.test(ua) && touchPoints > 1) return undefined;
  return /Windows|Macintosh|X11|CrOS|Linux/.test(ua) ? 'desktop' : undefined;
}
/** The phone's system, to put its own steps first. */
export const phoneOs = (ua: string): 'ios' | 'android' | undefined => (/iPhone|iPad|iPod/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : undefined);

const KEY = (k: BrowserHintKind) => `rounds.hint.${k}`;
const shownThisVisit = new Set<BrowserHintKind>();
export function hintSeen(kind: BrowserHintKind): boolean {
  if (shownThisVisit.has(kind)) return true;
  try { return localStorage.getItem(KEY(kind)) === '1'; } catch { return false; }
}
export function markHintSeen(kind: BrowserHintKind): void {
  shownThisVisit.add(kind);
  try { localStorage.setItem(KEY(kind), '1'); } catch { /* private mode or blocked storage: the memory copy covers this visit */ }
}
/** For tests: forget this visit's memory (localStorage is cleared by the test itself). */
export function resetBrowserHintsForTests(): void { shownThisVisit.clear(); }

const agent = () => (typeof navigator === 'undefined' ? { ua: '', touch: 0 } : { ua: navigator.userAgent ?? '', touch: navigator.maxTouchPoints ?? 0 });

/**
 * The hint this page should offer, decided once when the page opens: none when it has been shown on this device before.
 * It counts as shown the moment it appears, so it never comes back on the next screen or the next visit; `dismiss` hides it.
 * `enabled` false waits (the game shows it only in the lobby, never over a question).
 */
export function useBrowserHint(enabled = true): { kind?: BrowserHintKind; os?: 'ios' | 'android'; dismiss: () => void } {
  const [{ kind, os }] = useState(() => {
    const a = agent(); const k = browserHintKind(a.ua, a.touch);
    return { kind: k && !hintSeen(k) ? k : undefined, os: phoneOs(a.ua) };
  });
  const [open, setOpen] = useState(true);
  const showing = enabled && open && kind !== undefined;
  useEffect(() => { if (showing && kind) markHintSeen(kind); }, [showing, kind]);
  return { kind: showing ? kind : undefined, os, dismiss: () => setOpen(false) };
}

/** The hint's words. Steps for both phones, this phone's first; Instagram's own menu labels as they appear. */
export const HINT = {
  instagram: {
    title: 'Steadier in your browser',
    body: "Instagram's browser can drop the game when you flip back to the Live. Your own browser holds on.",
    carry: 'Your place comes with you.',
    ios: 'iPhone: tap ••• at the top right, then Open in external browser.',
    android: 'Android: tap ⋮ at the top right, then open it in Chrome or your usual browser.',
    ok: 'Got it',
  },
  tiktok: {
    title: 'Steadier in your browser',
    body: "TikTok's browser can drop the game when you flip back to the Live. Your own browser holds on.",
    carry: 'Your place comes with you.',
    ios: 'iPhone: tap ••• at the top right, then Open in browser.',
    android: 'Android: tap ⋮ at the top right, then Open in browser.',
    ok: 'Got it',
  },
  desktop: {
    title: 'On a computer?',
    join: 'Scan to play on your phone instead. It makes a better buzzer.',
    play: 'Scan to carry on on your phone. Your place comes with you.',
    qr: 'QR code to carry on on your phone',
    ok: 'Stay on this computer',
  },
} as const;

/**
 * The card itself. `where` is the join page (before joining: the QR opens the join link) or the game (the page's own
 * address carries this player's place, so the phone picks up where the computer left off).
 */
export function OpenInBrowser({ kind, os, url, where, onClose, className = '' }: {
  kind: BrowserHintKind; os?: 'ios' | 'android'; url: string; where: 'join' | 'play'; onClose: () => void; className?: string;
}) {
  if (kind === 'instagram' || kind === 'tiktok') {
    const w = HINT[kind];
    const steps = os === 'android' ? [w.android, w.ios] : [w.ios, w.android];
    return (
      <section aria-label={w.title} data-part="browser-hint" className={`flex flex-col gap-2.5 rounded-card border border-line bg-surface p-4 text-[15px] leading-snug text-ink shadow-card ${className}`}>
        <h2 className="flex items-center gap-2 font-display text-base font-extrabold"><ExternalLink aria-hidden="true" className="size-4 text-brand-strong" strokeWidth={2.4} />{w.title}</h2>
        <p className="text-ink-2">{w.body}{where === 'play' ? ` ${w.carry}` : ''}</p>
        <ul className="flex flex-col gap-1 text-ink">{steps.map((s) => <li key={s}>{s}</li>)}</ul>
        <button type="button" onClick={onClose} className="-mb-1 min-h-11 self-start font-semibold text-iv-strong underline-offset-4 hover:underline">{w.ok}</button>
      </section>
    );
  }
  return (
    <section aria-label={HINT.desktop.title} data-part="browser-hint" className={`flex items-center gap-4 rounded-card border border-line bg-surface p-4 text-[15px] leading-snug text-ink shadow-card ${className}`}>
      <div className="shrink-0 rounded-tile border border-line bg-white p-1.5"><Qr value={url} size={104} label={HINT.desktop.qr} /></div>
      <div className="flex min-w-0 flex-col gap-1.5">
        <h2 className="flex items-center gap-2 font-display text-base font-extrabold"><Smartphone aria-hidden="true" className="size-4 text-brand-strong" strokeWidth={2.4} />{HINT.desktop.title}</h2>
        <p className="text-ink-2">{where === 'join' ? HINT.desktop.join : HINT.desktop.play}</p>
        <button type="button" onClick={onClose} className="-mb-1 min-h-11 self-start font-semibold text-iv-strong underline-offset-4 hover:underline">{HINT.desktop.ok}</button>
      </div>
    </section>
  );
}
